// ==================== KEYWORD BOT STATS COMPONENT ====================

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bot, Zap, ArrowRight, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { staggerItem } from '../../../utils/animations';
import type { KeywordBotAnalytics } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface KeywordBotStatsProps {
  data: KeywordBotAnalytics | null;
  isLoading?: boolean;
}

// ==================== MINI CHART ====================

const TriggerSparkline = ({ data }: { data: Array<{ date: string; count: number }> }) => {
  if (!data || data.length === 0) return null;
  
  const maxCount = Math.max(...data.map(d => d.count), 1);
  
  return (
    <div className="flex items-end gap-px h-8">
      {data.slice(-7).map((day) => (
        <div
          key={day.date}
          className="flex-1 bg-(--color-signal-orange) rounded-t opacity-60 hover:opacity-100 transition-opacity"
          style={{ 
            height: `${Math.max((day.count / maxCount) * 100, 10)}%`,
            minHeight: day.count > 0 ? '4px' : '2px',
          }}
          title={`${day.date}: ${day.count} triggers`}
        />
      ))}
    </div>
  );
};

// ==================== COMPONENT ====================

export const KeywordBotStats = ({ data, isLoading = false }: KeywordBotStatsProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">Keyword Bots</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-2 animate-pulse">
              <div className="h-10 bg-(--color-border-gray) rounded" />
              <div className="h-20 bg-(--color-border-gray) rounded" />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  if (!data || data.totalBots === 0) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">Keyword Bots</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="py-4 text-center">
              <Bot size={24} className="text-(--color-muted-gray) mx-auto mb-1" />
              <p className="text-xs text-(--color-muted-gray)">No bots configured</p>
              <Link to="/app/keyword-bots" className="text-xs text-(--color-signal-orange) hover:underline mt-1 inline-block">
                Create a bot
              </Link>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div variants={staggerItem}>
      <Card>
        <CardHeader className="border-b-0 pb-1 mb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-1.5">
              <Bot size={14} className="text-cyan-400" />
              Keyword Bots
            </CardTitle>
            <Link 
              to="/app/keyword-bots" 
              className="flex items-center gap-1 text-xs text-(--color-signal-orange) hover:underline"
            >
              Manage
              <ArrowRight size={10} />
            </Link>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-3">
            {/* Stats Row */}
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <p className="text-lg font-bold text-white">{data.totalBots}</p>
                <p className="text-[10px] text-(--color-muted-gray)">Total</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <p className="text-lg font-bold text-green-400">{data.activeBots}</p>
                <p className="text-[10px] text-(--color-muted-gray)">Active</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <p className="text-lg font-bold text-(--color-signal-orange)">{data.totalTriggers}</p>
                <p className="text-[10px] text-(--color-muted-gray)">Triggers</p>
              </div>
            </div>

            {/* Sparkline */}
            {data.triggersByDay.length > 0 && (
              <div className="p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <p className="text-[10px] text-(--color-muted-gray) mb-1">Triggers (7 days)</p>
                <TriggerSparkline data={data.triggersByDay} />
              </div>
            )}

            {/* Top Bots */}
            {data.topBots.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] text-(--color-muted-gray)">Top Bots</p>
                {data.topBots.slice(0, 3).map(bot => (
                  <div 
                    key={bot.id}
                    className="flex items-center justify-between py-1 px-2 rounded bg-(--color-navy-light)"
                  >
                    <div className="flex items-center gap-1.5">
                      {bot.isActive ? (
                        <CheckCircle2 size={10} className="text-green-400" />
                      ) : (
                        <XCircle size={10} className="text-(--color-muted-gray)" />
                      )}
                      <span className="text-xs text-white truncate max-w-24">{bot.name}</span>
                    </div>
                    <div className="flex items-center gap-1 text-[10px] text-(--color-signal-orange)">
                      <Zap size={10} />
                      {bot.triggerCount}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default KeywordBotStats;
